import { formatPropertyAddress } from '../utils/propertyAddress';
import './MobileMapDisclosure.css';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import './IDXTownPage.css';

const IDXTownPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?zip=74037&state=${SITE_CONFIG.STATE}&firstPhoto=true&hasMedia=true&listingDateSort=newold`, {});
        
        if (response.ok) {
          const data = await response.json();
          setProperties(data.properties || []);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>IDXTown Homes for Sale | Modern Dream Homes in Tulsa | Andy Osburn</title>
        <meta name="description" content="Discover IDXTown homes for sale in Tulsa's most vibrant, walkable community. Modern Craftsman homes, top schools, 15 minutes from downtown. Contact Andy Osburn today." />
        <meta name="keywords" content="IDXTown homes for sale, Tulsa real estate, modern homes Tulsa, Northridge School District, Andy Osburn realtor" />
      </Helmet>
      
      <div className="idxtown-container">
        <button 
          onClick={() => navigate('/')}
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            padding: '10px 20px',
            background: 'rgb(14, 165, 233)',
            color: 'rgb(243, 244, 246)',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          Home
        </button>
        <header className="idxtown-header">
          <h1>IDXTown Homes for Sale: Find Your Modern Dream Home in This Vibrant, Walkable Community</h1>
        </header>
        
        <section className="idxtown-vibe">
          <h2>The IDXTown Vibe: Tulsa's Hub for Modern Living</h2>
          <p>
            Welcome to IDXTown, the neighborhood that perfectly blends urban convenience with established, family-friendly charm. Located just 15 minutes north of Downtown Tulsa, IDXTown has quickly become the preferred destination for young professionals and families seeking a walkable, dynamic community.
          </p>
          <p>
            The architecture here is distinctly modern, featuring custom-built Craftsman-style homes and contemporary townhomes, many of which were built within the last five years. Unlike older areas of the city, IDXTown boasts consistent, wide sidewalks, underground utilities, and well-maintained green spaces, giving it a clean, polished feel.
          </p>
        </section>
        
        <section className="idxtown-lifestyle">
          <h2>Local Lifestyle and Amenities (The Daily IDXTown Experience)</h2>
          <p>Life in IDXTown revolves around convenience and community connection.</p>
          
          <div className="amenities-list">
            <div className="amenity">
              <h3>The Corner Café</h3>
              <p>The heart of the neighborhood is the "IDXTown Square," anchored by the beloved The Corner Café, a popular spot for morning coffee and evening gatherings.</p>
            </div>
            
            <div className="amenity">
              <h3>The GreenWay Park</h3>
              <p>Families benefit from the expansive GreenWay Park, which offers a splash pad, covered picnic areas, and direct access to the Arkansas River Trail system.</p>
            </div>
            
            <div className="amenity">
              <h3>Top-Rated Schools</h3>
              <p>IDXTown is proudly zoned for the Northridge School District, a primary driver for families moving into the area.</p>
            </div>
            
            <div className="amenity">
              <h3>Commuting</h3>
              <p>Our location provides seamless access to I-44 and US-169, making the drive to major employers like the Tulsa Airport Technology Center or the BOK Tower quick and efficient.</p>
            </div>
          </div>
        </section>
        
        <section className="market-snapshot">
          <h2>Current IDXTown Real Estate Market Snapshot</h2>
          <p>The IDXTown real estate market is highly competitive due to the quality of the homes and the school district. Most listings feature 3- to 4-bedroom layouts with premium amenities.</p>
          
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Statistic</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Median Home Price</td>
                <td>$415,000</td>
              </tr>
              <tr>
                <td>Average Days on Market</td>
                <td>21 Days (Well below the Tulsa Metro average)</td>
              </tr>
              <tr>
                <td>Popular Home Types</td>
                <td>New Construction, Modern Farmhouse, Townhomes</td>
              </tr>
            </tbody>
          </table>
        </section>
        
        <section className="properties-section">
          <h2>Current IDXTown Properties for Sale</h2>
          {loading ? (
            <p>Loading properties...</p>
          ) : (
            <div className="properties-grid">
              {properties.slice(0, 12).map((property) => (
                <div 
                  key={property.id} 
                  className="property-card"
                  onClick={() => window.location.href = `/listing/${property.listing_key}`}
                  style={{
                    backgroundImage: property.first_photo_url ? `url(${property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">
                    {formatPropertyAddress(property)}
                  </div>
                  <div className="property-type">{property.property_sub_type}</div>
                </div>
              ))}
            </div>
          )}
        </section>
        
        <section className="cta-section">
          <h2>Ready to Explore IDXTown Homes?</h2>
          <p>Contact Andy Osburn, your trusted Tulsa Metro real estate expert, to schedule a tour of available IDXTown properties.</p>
          <div className="cta-buttons">
            <a href="mailto:osburnandy@gmail.com?subject=IDXTown Home Inquiry" className="cta-button primary">
              Email Andy
            </a>
            <a href="tel:(918) 637-7826" className="cta-button secondary">
              Call (918) 637-7826
            </a>
          </div>
        </section>
      </div>
      <div className="mobile-map-disclosure" style={{background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
        <img src={SITE_CONFIG.MLS.LOGO_URL} alt={SITE_CONFIG.MLS.SHORT_NAME} style={{height: '20px', objectFit: 'contain'}} />
        <span style={{fontSize: '10px', color: '#ccc', flex: 1}}>{SITE_CONFIG.MLS.DISCLAIMER} | Brokerage License: {SITE_CONFIG.AGENT.BROKERAGE_LICENSE} | Agent License: {SITE_CONFIG.AGENT.LICENSE}</span>
      </div>
    </div>
  );
};

export default IDXTownPage;